import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import AuthTabs from './components/AuthTabs';
import LoginForm from './components/LoginForm';
import RegisterForm from './components/RegisterForm';
import SocialLogin from './components/SocialLogin';
// WelcomeSection no longer used after full remodel
import { useAuth } from '../../hooks/useAuth';
import Icon from '../../components/AppIcon';

const LoginRegister = () => {
  const [activeTab, setActiveTab] = useState('login');
  const navigate = useNavigate();
  const { isAuthenticated, isLoading } = useAuth();

  useEffect(() => {
    // Redirect to dashboard if user is already authenticated
    if (isAuthenticated && !isLoading) {
      navigate('/student-dashboard', { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Login & Register - UniSteps</title>
        <meta
          name="description"
          content="Sign in or create your UniSteps account to access premium admissions tools, build a standout portfolio, and connect with leading universities."
        />
      </Helmet>

      <div className="min-h-screen app-shell pt-4 sm:pt-6 pb-8 sm:pb-12 safe-bottom mobile-scroll">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8">
          {/* Top hero section */}
          <div className="relative rounded-2xl sm:rounded-3xl overflow-hidden mb-6 sm:mb-8 fade-up">
            <div className="absolute inset-0 aurora-blob opacity-70" aria-hidden="true" />
            <div className="grid lg:grid-cols-2 gap-4 sm:gap-6 items-center relative z-10 p-4 sm:p-6 lg:p-10">
              <div className="space-y-3 sm:space-y-4">
                <p className="text-[10px] sm:text-xs uppercase tracking-[0.25em] sm:tracking-[0.28em] text-muted-foreground">Welcome to UniSteps</p>
                <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold leading-tight text-foreground">
                  Admissions tools you’ll actually enjoy using
                </h1>
                <p className="text-lg text-muted-foreground max-w-2xl">
                  Calm, precise interfaces for portfolios, deadlines, and discovery — designed to keep you focused.
                </p>
                <div className="flex flex-wrap items-center gap-3 pt-1">
                  <span className="pill bg-primary/10 text-primary">AI onboarding</span>
                  <span className="pill bg-accent/15 text-foreground">Workshops</span>
                  <span className="pill bg-secondary/20 text-secondary-foreground">Portfolio builder</span>
                </div>
              </div>

              <div className="surface-card rounded-2xl p-6 lg:p-7 shadow-xl lifted max-w-lg w-full lg:justify-self-end h-full flex flex-col lg:sticky lg:top-10 self-start">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Account Access</p>
                    <p className="text-xl font-semibold text-foreground">Join or continue your journey</p>
                  </div>
                  <div className="w-12 h-12 rounded-xl bg-primary/15 flex items-center justify-center">
                    <span className="text-primary text-lg font-semibold">↗</span>
                  </div>
                </div>

                <AuthTabs activeTab={activeTab} onTabChange={setActiveTab} />

                <div className="mb-6 mt-4">
                  {activeTab === 'login' ? <LoginForm /> : <RegisterForm />}
                </div>

                <SocialLogin />

                {activeTab === 'login' ? (
                  <p className="text-center text-sm text-muted-foreground mt-6">
                    Don't have an account?{' '}
                    <button
                      onClick={() => setActiveTab('register')}
                      className="text-primary hover:text-primary/80 font-semibold transition-hover"
                    >
                      Sign up now
                    </button>
                  </p>
                ) : (
                  <p className="text-center text-sm text-muted-foreground mt-6">
                    Already have an account?{' '}
                    <button
                      onClick={() => setActiveTab('login')}
                      className="text-primary hover:text-primary/80 font-semibold transition-hover"
                    >
                      Sign in
                    </button>
                  </p>
                )}

                {/* Trust & UX badges */}
                <div className="mt-6 flex flex-wrap items-center justify-center gap-2 text-xs text-muted-foreground">
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full glass-panel glass-stroke">
                    <Icon name="ShieldCheck" size={14} /> Secure login
                  </span>
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full glass-panel glass-stroke">
                    <Icon name="Lock" size={14} /> Password protected
                  </span>
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full glass-panel glass-stroke">
                    <Icon name="UserCheck" size={14} /> You control your data
                  </span>
                </div>
              </div>
            </div>
          </div>
          {/* Lower benefits & help section */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8 max-w-6xl items-start fade-up">
            <div className="surface-card rounded-2xl p-6 lg:p-8 lifted">
              <h2 className="text-2xl font-semibold text-foreground mb-3">Why UniSteps</h2>
              <p className="text-muted-foreground mb-4">Verified content and clear guidance across your admissions journey.</p>
              <div className="grid sm:grid-cols-2 gap-4">
                {["Portfolio intelligence", "University discovery", "Workshop mastery", "Document vault"].map((item) => (
                  <div key={item} className="glass-panel glass-stroke rounded-xl px-4 py-3 flex items-center gap-3">
                    <span className="w-10 h-10 rounded-lg bg-primary/15 flex items-center justify-center text-primary font-semibold">✓</span>
                    <p className="font-medium text-foreground">{item}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="surface-card rounded-2xl p-6 lg:p-8 lifted">
              <h3 className="text-xl font-semibold text-foreground mb-3">Privacy & control</h3>
              <p className="text-muted-foreground mb-5">You own your data. We use secure auth and storage.</p>
              <div className="space-y-3">
                {["Secure authentication", "Granular portfolio controls", "Export any time"].map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <span className="w-9 h-9 rounded-lg bg-secondary/20 flex items-center justify-center text-secondary-foreground">🔒</span>
                    <p className="text-foreground">{item}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="surface-card rounded-2xl p-6 lg:p-8 lifted">
              <h3 className="text-xl font-semibold text-foreground mb-3">Get help</h3>
              <p className="text-muted-foreground mb-5">Quick answers when you need them.</p>
              <div className="grid sm:grid-cols-2 gap-4">
                {[{label: 'Reset password', icon: 'KeyRound'}, {label: 'Contact support', icon: 'LifeBuoy'}, {label: 'Guides', icon: 'BookOpen'}, {label: 'FAQ', icon: 'HelpCircle'}].map((link) => (
                  <a key={link.label} href="#" className="glass-panel glass-stroke rounded-xl px-4 py-3 flex items-center gap-3 hover:lifted transition-smooth">
                    <span className="w-10 h-10 rounded-lg bg-primary/15 flex items-center justify-center text-primary">
                      <Icon name={link.icon} size={16} />
                    </span>
                    <span className="font-medium text-foreground">{link.label}</span>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LoginRegister;